import * as React from "react";
import Context, { Config, Filter, Actions } from "../../context";

import sortData from "./sortData";
import filterData from "./filterData";

function isSet(value: any) {
  return value !== null && value !== undefined;
}

type ColumnConfigurer = {
  __get: () => Config[];
};

type Without<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

type State = {
  sortKey: string | null;
  sortDirection: "asc" | "desc";
  filters: Record<string, Filter>;
  showMap: Record<string, boolean>;
  memoData: any[] | null | undefined;
  memoColumns: Config[];
  hoveredRowIndex: number | null;
};

export type TableState = Without<State, "memoData" | "hoveredRowIndex">;

const TABLE_STATE_KEYS: Array<keyof TableState> = [
  "sortKey",
  "sortDirection",
  "filters",
  "showMap",
  "memoColumns"
];

function didTableStateChange(a: TableState, b: TableState) {
  return TABLE_STATE_KEYS.some(key => {
    if (a[key] !== b[key]) {
      return true;
    }

    return false;
  });
}

type Props = {
  children?: any;
  columns: ColumnConfigurer;
  t: any;
  data?: any[] | null;
  rowColor1?: string;
  rowColor2?: string;
  selectedRows?: string[];
  onSelectedRowsChange?: (rows: string[]) => void;
  actions?: Actions;
  onRowClick?: (row: any) => void;
  tableState?: TableState;
  onTableStateChange?: (tableState: TableState) => void;
};

export type SortState = Pick<State, "sortKey" | "sortDirection">;

function initializeFilterState(columns: ColumnConfigurer) {
  const configs = columns.__get();

  return configs.reduce((filterState, config) => {
    return {
      ...filterState,
      [config.key]: {
        show: false,
        value: null
      }
    };
  }, {});
}

function initializeShowMap(columns: ColumnConfigurer) {
  const configs = columns.__get();

  return configs.reduce((showMap, config) => {
    return {
      ...showMap,
      [config.key]: config.showInitial
    };
  }, {});
}

export function initializeTableState(columns: ColumnConfigurer): TableState {
  return {
    sortKey: null,
    sortDirection: "asc",
    filters: initializeFilterState(columns),
    showMap: initializeShowMap(columns),
    memoColumns: columns.__get().filter(x => x.showInitial)
  };
}

class Provider extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    if (this.isControlled()) {
      this.state = {
        ...this.props.tableState!,
        memoData: props.data
          ? this.processData(props.data, this.props.tableState as any)
          : props.data,
        hoveredRowIndex: null
      };
    } else {
      this.state = {
        ...initializeTableState(props.columns),
        memoData: props.data,
        hoveredRowIndex: null
      };
    }
  }

  isControlled() {
    return isSet(this.props.tableState) && isSet(this.props.onTableStateChange);
  }

  grabExternalTableState() {
    const { hoveredRowIndex, memoData, ...state } = this.state;
    return state;
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.data !== prevProps.data) {
      this.setState({
        memoData: this.props.data
          ? this.processData(this.props.data, this.state)
          : null
      });
    }

    // keep new external tableState in sync with local state
    if (this.isControlled()) {
      if (!didTableStateChange(prevProps.tableState!, this.props.tableState!)) {
        return;
      }

      const propState = this.props.tableState!;
      if (didTableStateChange(propState, this.state)) {
        this.setState({ ...propState });
      }
    }
  }

  handleShowColumn = (key: string, show: boolean) => {
    const { filters: currentFilters, showMap, memoData } = this.state;

    const additionalState = {
      filters: currentFilters,
      memoData
    };
    if (!show && currentFilters[key].show) {
      additionalState.filters = {
        ...currentFilters,
        [key]: {
          show: false,
          value: null
        }
      };
      additionalState.memoData = this.processData(this.props.data || [], {
        ...this.state,
        ...additionalState
      });
    }

    const newShowMap = {
      ...showMap,
      [key]: show
    };

    this.setState(
      {
        showMap: newShowMap,
        memoColumns: this.props.columns
          .__get()
          .filter(x => newShowMap[x.key as string]),
        ...additionalState
      },
      () => {
        if (this.isControlled()) {
          this.props.onTableStateChange!(this.grabExternalTableState());
        }
      }
    );
  };

  handleToggleSort = (key: string) => {
    const { sortKey, sortDirection } = this.state;

    let newState = this.state;

    if (sortKey === key && sortDirection === "desc") {
      newState = {
        ...this.state,
        sortKey: null,
        sortDirection: "asc"
      };
    } else if (sortKey === key && sortDirection === "asc") {
      newState = { ...this.state, sortDirection: "desc" };
    } else if (sortKey !== key) {
      newState = {
        ...this.state,
        sortKey: key,
        sortDirection: "asc"
      };
    }

    this.setState(
      {
        ...newState,
        memoData: this.processData(this.props.data || [], newState)
      },
      () => {
        if (this.isControlled()) {
          this.props.onTableStateChange!(this.grabExternalTableState());
        }
      }
    );
  };

  getFilter = (key: string) => {
    return this.state.filters[key];
  };

  updateFilterState = (key: string, partialState: Partial<Filter>) => {
    const { filters } = this.state;

    const newState = {
      ...this.state,
      filters: {
        ...filters,
        [key]: {
          ...filters[key],
          ...partialState
        }
      }
    };

    this.setState(
      {
        ...newState,
        memoData: this.processData(this.props.data || [], newState)
      },
      () => {
        if (this.isControlled()) {
          this.props.onTableStateChange!(this.grabExternalTableState());
        }
      }
    );
  };

  setShowFilter = (key: string, show: boolean) => {
    const currentFilter = this.state.filters[key];
    const config = this.props.columns.__get().find(x => x.key === key)!;

    let value = currentFilter.value;

    if (show && !currentFilter.show) {
      value = config.initialFilterValue;
    } else if (currentFilter.show && !show) {
      value = null;
    }

    this.updateFilterState(key, { show, value });
  };

  setFilterValue = (key: string, value: any) => {
    this.updateFilterState(key, { value });
  };

  processData = (data: any[], state: State): any[] => {
    const { columns, t } = this.props;

    const filteredData = filterData(data, state.filters, columns.__get(), t);

    return sortData(filteredData, state, columns.__get(), t);
  };

  handleSetFilters = (filterMap: Record<string, any>) => {
    const { showMap } = this.state;
    // filter out keys of columns that are not currently shown
    // create new filter state
    const newFilterState = {
      ...initializeFilterState(this.props.columns),
      ...Object.keys(filterMap)
        .filter(key => showMap[key])
        .reduce(
          (filters, key) => ({
            ...filters,
            [key]: { show: true, value: filterMap[key] }
          }),
          {}
        )
    };

    this.setState(
      {
        memoData: this.processData(this.props.data || [], {
          ...this.state,
          filters: newFilterState
        }),
        filters: newFilterState
      },
      () => {
        if (this.isControlled()) {
          this.props.onTableStateChange!(this.grabExternalTableState());
        }
      }
    );
  };

  setHoveredRowIndex = (index: number | null) => {
    this.setState({ hoveredRowIndex: index });
  };

  render() {
    const {
      children,
      data,
      rowColor1,
      rowColor2,
      columns,
      onSelectedRowsChange,
      selectedRows,
      actions,
      ...rest
    } = this.props;

    const { memoData, memoColumns, hoveredRowIndex, ...state } = this.state;
    const columnConfigs = columns.__get();

    const hasBulkSelect =
      Boolean(onSelectedRowsChange) && Boolean(selectedRows);

    return (
      <Context.Provider
        value={{
          ...rest,
          data: memoData || [],
          rowColor1: rowColor1 || "hsl(0, 0%, 96%)",
          rowColor2: rowColor2 || "white",
          columnConfig: memoColumns,
          columnConfigAll: columnConfigs,
          toggleSort: this.handleToggleSort,
          getFilter: this.getFilter,
          setFilterShow: this.setShowFilter,
          setFilterValue: this.setFilterValue,
          setFilters: this.handleSetFilters,
          setShowColumn: this.handleShowColumn,
          selectedRows,
          onSelectedRowsChange,
          hasBulkSelect,
          actions,
          hoveredRowIndex,
          setHoveredRowIndex: this.setHoveredRowIndex,
          isLoading: !memoData,
          ...state
        }}
      >
        {children}
      </Context.Provider>
    );
  }
}

export default Provider;
